"use client";

import React from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import gql from "graphql-tag";
import { useSnackbar } from "notistack";
import Image from "next/image";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { addOrUpdateItemInCart, getCart } from "@/redux/features/cart/cartSlice";
import { CartPricingMode, SCartDto } from "@/__generated__/sdk";
import { GqlClient } from "@/Providers/gqlClients/GqlClient";
import { createGqlClient } from "@/Providers/gqlClients/BaseGqlClient";
import { PRICING_MODE_LABEL } from "@/lib/pricingMode";
import getError from "@/lib/getError";
import { sendAddToCartEvent } from "../sendAddToCartEvent";

const ConvertCartPricingModeDocument = gql`
  mutation ConvertCartPricingMode($pricingMode: CartPricingMode!) {
    convertCartPricingMode(pricingMode: $pricingMode) {
      id
      pricingMode
    }
  }
`;

export type PricingModeGuideOffer = {
  label: string;
  mode: CartPricingMode;
  price: number;
  variantId?: number;
  maxBasket?: number;
  shippingOrigin?: string | null;
};

export type PricingModeGuideRequest = {
  cartMode: CartPricingMode;
  quantity: number;
  productId: string;
  clicked: PricingModeGuideOffer;
  suggestion: (PricingModeGuideOffer & { variantId: number }) | null;
};

type Props = {
  request: PricingModeGuideRequest | null;
  productTitle: string;
  imageSrc?: string | null;
  onClose: () => void;
};

const BottomSheetTransition = React.forwardRef(function BottomSheetTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function toman(value: number) {
  return `${Math.round(value).toLocaleString("fa-IR")} تومان`;
}

/** Converts the open cart. Sends the user token when the shopper is signed in. */
async function convertOpenCart(mode: CartPricingMode, accessToken?: string | null) {
  const client = createGqlClient().client;
  await client.request<{ convertCartPricingMode: Pick<SCartDto, "id" | "pricingMode"> }>(
    ConvertCartPricingModeDocument,
    { pricingMode: mode },
    accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
  );
}

function ModeBadge({ mode, size = "md" }: { mode: CartPricingMode; size?: "sm" | "md" }) {
  const isCredit = mode === CartPricingMode.Credit;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: size === "sm" ? 0.75 : 1,
        py: size === "sm" ? 0.2 : 0.4,
        borderRadius: 1.5,
        fontWeight: 800,
        fontSize: size === "sm" ? "0.75rem" : "0.9rem",
        lineHeight: 1.4,
        bgcolor: isCredit ? "info.50" : "grey.100",
        color: isCredit ? "info.main" : "grey.900",
        border: "1px solid",
        borderColor: isCredit ? "info.light" : "grey.300",
      }}
    >
      {PRICING_MODE_LABEL[mode]}
    </Box>
  );
}

function ChoiceCard({
  step,
  title,
  seller,
  price,
  consequence,
  badge,
  tone,
  loading,
  disabled,
  onClick,
}: {
  step: string;
  title: string;
  seller: string;
  price: number;
  consequence: string;
  badge?: string;
  /** soft = keep cart; strong = convert; solid = sole CTA */
  tone: "soft" | "strong" | "solid";
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const toneSx =
    tone === "solid"
      ? {
          bgcolor: "primary.main",
          color: "primary.contrastText",
          borderColor: "primary.main",
          "&:hover": { bgcolor: "grey.800", borderColor: "grey.800" },
          "& .choice-step": { bgcolor: "rgba(255,255,255,0.18)", color: "#fff" },
          "& .choice-meta": { color: "rgba(255,255,255,0.78)" },
          "& .choice-price": { color: "#fff" },
          "& .choice-note": { color: "rgba(255,255,255,0.8)" },
        }
      : tone === "soft"
        ? {
            bgcolor: "secondary.50",
            color: "grey.900",
            borderColor: "secondary.main",
            "&:hover": { bgcolor: "secondary.100", borderColor: "secondary.700" },
            "& .choice-step": { bgcolor: "secondary.main", color: "grey.900" },
            "& .choice-meta": { color: "grey.700" },
            "& .choice-price": { color: "grey.900" },
            "& .choice-note": { color: "success.main" },
          }
        : {
            bgcolor: "#fff",
            color: "grey.900",
            borderColor: "grey.800",
            "&:hover": { bgcolor: "grey.50", borderColor: "grey.900" },
            "& .choice-step": { bgcolor: "grey.900", color: "#fff" },
            "& .choice-meta": { color: "grey.700" },
            "& .choice-price": { color: "grey.900" },
            "& .choice-note": { color: "warning.main" },
          };

  return (
    <Button
      fullWidth
      disableElevation
      disabled={disabled || loading}
      onClick={onClick}
      aria-busy={loading || undefined}
      sx={{
        display: "block",
        position: "relative",
        textAlign: "start",
        textTransform: "none",
        px: 1.5,
        py: 1.5,
        borderRadius: 2.5,
        border: "1.5px solid",
        lineHeight: 1.4,
        transition: "opacity 120ms ease, background-color 120ms ease, border-color 120ms ease",
        ...toneSx,
        "&.Mui-disabled": {
          opacity: loading ? 1 : 0.45,
          bgcolor: toneSx.bgcolor,
          color: toneSx.color,
          borderColor: toneSx.borderColor,
        },
      }}
    >
      {loading ? (
        <CircularProgress
          size={18}
          thickness={5}
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            color: tone === "solid" ? "#fff" : "secondary.800",
          }}
        />
      ) : null}

      <Box sx={{ opacity: loading ? 0.55 : disabled ? 0.5 : 1, transition: "opacity 120ms ease" }}>
        <Stack direction="row" alignItems="flex-start" gap={1}>
          <Box
            className="choice-step"
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              fontWeight: 800,
              fontSize: "0.85rem",
              lineHeight: 1,
              mt: 0.1,
            }}
          >
            {step}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography fontWeight={800} sx={{ fontSize: "0.95rem", lineHeight: 1.45, color: "inherit" }}>
                {title}
              </Typography>
              {badge ? (
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    px: 0.75,
                    py: 0.2,
                    borderRadius: 1,
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    bgcolor: "secondary.main",
                    color: "grey.900",
                    lineHeight: 1.4,
                  }}
                >
                  {badge}
                </Box>
              ) : null}
            </Stack>

            <Stack direction="row" alignItems="center" gap={0.5} className="choice-meta" sx={{ mt: 0.75, minWidth: 0 }}>
              <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography noWrap sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
                از «{seller}» · {toman(price)}
              </Typography>
            </Stack>

            <Typography className="choice-note" sx={{ mt: 0.85, fontSize: "0.78rem", fontWeight: 800, lineHeight: 1.6 }}>
              {consequence}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Button>
  );
}

export default function CartModeGuideDialog({ request, productTitle, imageSrc, onClose }: Props) {
  const dispatch = useAppDispatch();
  const session = useAppSelector((state) => state.session.session);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const suggestion = request?.suggestion ?? null;
  const cartLabel = request ? PRICING_MODE_LABEL[request.cartMode] : "";
  const clickedLabel = request ? PRICING_MODE_LABEL[request.clicked.mode] : "";

  const { mutate: addSuggestion, isPending } = useMutation({
    mutationFn: async () => {
      if (!request?.suggestion) return null;
      const maxBasket = request.suggestion.maxBasket ?? 0;
      if (maxBasket > 0 && request.quantity > maxBasket) {
        throw new Error("موجودی این فروشنده کافی نیست");
      }
      return dispatch(
        addOrUpdateItemInCart({
          createCartInput: {
            quantity: request.quantity,
            variantId: request.suggestion.variantId,
            productId: request.productId,
            pricingMode: request.suggestion.mode,
          },
        }),
      ).unwrap();
    },
    onSuccess: (data) => {
      if (!data || !request?.suggestion) return;
      const item = data.cartItems.find(
        (line) => line.variantId === request.suggestion?.variantId && line.productId === request.productId,
      );
      if (item) sendAddToCartEvent(item);
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(getError(error) || "افزودن به سبد انجام نشد", { variant: "error" });
    },
  });

  const { mutate: switchWholeCart, isPending: isSwitching } = useMutation({
    mutationFn: async () => {
      if (!request?.clicked.variantId) {
        throw new Error("فروشنده انتخاب‌شده پیدا نشد");
      }
      const target = request.clicked.mode;
      const maxBasket = request.clicked.maxBasket ?? 0;
      if (maxBasket > 0 && request.quantity > maxBasket) {
        throw new Error("موجودی این فروشنده کافی نیست");
      }

      const accessToken = session?.accessToken;
      if (accessToken) {
        try {
          const { gqlClient } = new GqlClient(session);
          await gqlClient.convertCurrentOrderPricingMode({ pricingMode: target });
        } catch (error) {
          const message = getError(error);
          if (message.includes("قیمت") || message.includes("موجود نیست")) throw error;
          await convertOpenCart(target, accessToken);
        }
      } else {
        await convertOpenCart(target);
      }

      return dispatch(
        addOrUpdateItemInCart({
          createCartInput: {
            quantity: request.quantity,
            variantId: request.clicked.variantId,
            productId: request.productId,
            pricingMode: target,
          },
        }),
      ).unwrap();
    },
    onSuccess: (data) => {
      if (!data || !request?.clicked.variantId) return;
      const item = data.cartItems.find(
        (line) => line.variantId === request.clicked.variantId && line.productId === request.productId,
      );
      if (item) sendAddToCartEvent(item);
      onClose();
    },
    onError: (error) => {
      dispatch(getCart());
      enqueueSnackbar(getError(error) || "تغییر سبد انجام نشد", { variant: "error" });
    },
  });

  const busy = isPending || isSwitching;

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={Boolean(request)}
      onClose={busy ? undefined : close}
      fullWidth
      maxWidth="xs"
      scroll="paper"
      TransitionComponent={isMobile ? BottomSheetTransition : undefined}
      sx={{
        "& .MuiDialog-container": {
          alignItems: isMobile ? "flex-end" : "center",
        },
      }}
      PaperProps={{
        sx: {
          m: isMobile ? 0 : 2,
          width: "100%",
          maxHeight: isMobile ? "92dvh" : "calc(100% - 64px)",
          borderRadius: isMobile ? "22px 22px 0 0" : 3,
          overflow: "hidden",
        },
      }}
    >
      {request ? (
        <>
          <DialogTitle sx={{ px: isMobile ? 1.5 : 2, pt: isMobile ? 0.75 : 2, pb: 0 }}>
            {isMobile ? (
              <Box sx={{ width: 36, height: 4, borderRadius: 4, bgcolor: "grey.300", mx: "auto", mb: 0.75 }} />
            ) : null}
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography
                component="span"
                sx={{ flex: 1, fontWeight: 800, fontSize: isMobile ? "1rem" : "1.1rem", lineHeight: 1.5, color: "grey.900" }}
              >
                صبر کن، قاطی شد!
              </Typography>
              <IconButton
                aria-label="بستن"
                onClick={close}
                disabled={busy}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid",
                  borderColor: "grey.300",
                  color: "grey.800",
                  "&.Mui-disabled": { opacity: 0.4 },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </DialogTitle>

          <DialogContent
            sx={{
              px: isMobile ? 1.5 : 2,
              pt: isMobile ? 1.25 : 1.5,
              pb: isMobile ? "calc(12px + env(safe-area-inset-bottom))" : 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                mb: 1.5,
                minWidth: 0,
                px: 1,
                py: 0.75,
                borderRadius: 2,
                bgcolor: "grey.A400",
                border: "1px solid",
                borderColor: "grey.100",
              }}
            >
              {imageSrc ? (
                <Box
                  sx={{
                    position: "relative",
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    flexShrink: 0,
                    bgcolor: "#fff",
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <Image src={imageSrc} alt="" fill unoptimized style={{ objectFit: "cover" }} />
                </Box>
              ) : null}
              <Typography noWrap fontWeight={700} sx={{ fontSize: "0.8rem", lineHeight: 1.5, color: "grey.800" }}>
                {productTitle}
              </Typography>
            </Stack>

            {/* Visual: cart vs clicked don't match */}
            <Box
              sx={{
                mb: 1.75,
                p: 1.25,
                borderRadius: 2.5,
                bgcolor: "error.light",
                border: "1px solid",
                borderColor: "error.100",
              }}
            >
              <Stack direction="row" alignItems="stretch" gap={1}>
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "grey.700", mb: 0.5 }}>
                    سبد الان
                  </Typography>
                  <ModeBadge mode={request.cartMode} />
                </Box>
                <Box
                  sx={{
                    alignSelf: "center",
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#fff",
                    border: "1px solid",
                    borderColor: "error.100",
                    color: "error.main",
                    fontWeight: 900,
                    fontSize: "0.85rem",
                    flexShrink: 0,
                  }}
                >
                  ≠
                </Box>
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "grey.700", mb: 0.5 }}>
                    چیزی که زدی
                  </Typography>
                  <ModeBadge mode={request.clicked.mode} />
                </Box>
              </Stack>
              <Typography
                sx={{
                  mt: 1.25,
                  textAlign: "center",
                  fontSize: "0.8rem",
                  fontWeight: 800,
                  lineHeight: 1.7,
                  color: "grey.900",
                }}
              >
                تو یک سبد، نقدی و اعتباری با هم نمی‌روند.
              </Typography>
            </Box>

            <Typography sx={{ mb: 1, fontSize: "0.85rem", fontWeight: 800, color: "grey.900" }}>
              یکی را بزن:
            </Typography>

            <Stack spacing={1}>
              {suggestion ? (
                <ChoiceCard
                  step="۱"
                  tone="soft"
                  badge="راحت‌تر"
                  title={`سبد ${cartLabel} بماند`}
                  seller={suggestion.label}
                  price={suggestion.price}
                  consequence="کالا اضافه می‌شود · بقیه سبد دست نمی‌خورد"
                  loading={isPending}
                  disabled={isSwitching}
                  onClick={() => addSuggestion()}
                />
              ) : (
                <Box
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: "warning.light",
                    border: "1px solid",
                    borderColor: "warning.100",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, lineHeight: 1.7, color: "warning.main" }}>
                    فروشنده {cartLabel} برای این کالا نداریم. فقط می‌توانی کل سبد را {clickedLabel} کنی.
                  </Typography>
                </Box>
              )}

              <ChoiceCard
                step={suggestion ? "۲" : "۱"}
                tone={suggestion ? "strong" : "solid"}
                title={`کل سبد ${clickedLabel} شود`}
                seller={request.clicked.label}
                price={request.clicked.price}
                consequence={`بقیه کالاها هم ${clickedLabel} می‌شوند`}
                loading={isSwitching}
                disabled={busy && !isSwitching}
                onClick={() => switchWholeCart()}
              />

              <Button
                fullWidth
                onClick={close}
                disabled={busy}
                sx={{
                  height: 40,
                  fontWeight: 800,
                  fontSize: "0.875rem",
                  color: "grey.700",
                  "&.Mui-disabled": { color: "grey.400" },
                }}
              >
                بی‌خیال، برگرد
              </Button>
            </Stack>
          </DialogContent>
        </>
      ) : null}
    </Dialog>
  );
}
